import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, PhoneCall } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useBrand } from "../../lib/BrandContext";

/**
 * DebtNavbar
 *
 * Dedicated top navigation for VenturaZ Money (the debt vertical).
 * Deliberately omits equity-side items (For Investors, Verified
 * Directory, Apply for Funding) so a borrower who lands on
 * /raise-debt is never nudged into the equity funnel — the two
 * journeys felt collapsed together in a way users found confusing.
 *
 * Structure:
 *   [Logo · VenturaZ Money]  [Loans ▾] [EMI Calculator] [How it Works] [FAQs] [Login/Dashboard] [Get free consultation]
 */
const LOAN_GROUPS = [
  {
    key: "individuals",
    heading: "For Individuals",
    items: [
      { hash: "#products", label: "Home Loan", sub: "₹5 L – ₹10 Cr · Rates from 8.35%" },
      { hash: "#products", label: "Car Loan", sub: "100% on-road funding" },
      { hash: "#products", label: "Personal Loan", sub: "Wedding · Vacation · Medical" },
      { hash: "#products", label: "Education Loan", sub: "No collateral up to ₹40 L" },
      { hash: "#products", label: "Gold Loan", sub: "Unlock value in 30 mins" },
      { hash: "#products", label: "Loan Against Property", sub: "Individual LAP" },
    ],
  },
  {
    key: "businesses",
    heading: "For Businesses",
    items: [
      { hash: "#products", label: "Working Capital", sub: "CC / OD lines" },
      { hash: "#products", label: "Term Loan", sub: "CapEx · Expansion" },
      { hash: "#products", label: "Unsecured Business Loan", sub: "MSME · 48hr approval" },
      { hash: "#products", label: "Invoice Discounting", sub: "Same-day cash" },
      { hash: "#products", label: "LAP (Business)", sub: "Against commercial property" },
      { hash: "#products", label: "Structured / RBF / Bridge", sub: "Private credit" },
    ],
  },
];

export default function DebtNavbar() {
  const [openMobile, setOpenMobile] = useState(false);
  const [openLoans, setOpenLoans] = useState(false);
  const rootRef = useRef(null);
  const { user, logout } = useAuth();
  const { logoUrl } = useBrand();
  const nav = useNavigate();

  const dashLink = user?.role === "investor"
    ? "/investor/dashboard"
    : user?.role === "startup"
      ? "/startup/dashboard"
      : "/admin/dashboard";

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenLoans(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const close = () => { setOpenLoans(false); setOpenMobile(false); };

  return (
    <header
      ref={rootRef}
      className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[#B8860B]/25"
      data-testid="debt-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        {/* Brand — includes a "← VenturaZ" back-link so the money vertical
            always has a one-click return path to the equity homepage. */}
        <div className="flex items-center gap-3">
          <Link to="/" onClick={close} className="text-[11px] font-heading text-ink-muted hover:text-[#7A4E1D] inline-flex items-center gap-1" data-testid="debt-nav-back-to-venturaz">
            ← VenturaZ
          </Link>
          <span className="text-ink-muted/40 text-xs">·</span>
          <Link to="/money" onClick={close} className="flex items-center gap-2" data-testid="debt-brand-link">
            {logoUrl ? (
              <img src={logoUrl} alt="VenturaZ" className="h-8 w-auto max-w-[180px] object-contain" data-testid="debt-brand-logo-img" />
            ) : (
              <div className="brand-mark text-2xl md:text-[26px] leading-none">
                <span className="word">VENTURAZ</span>
              </div>
            )}
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] ml-1" data-testid="debt-brand-vertical-badge">
              Money · Loan Advisor
            </span>
          </Link>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="debt-nav-links">
          {/* Loans dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setOpenLoans(true)}
            onMouseLeave={() => setOpenLoans(false)}
          >
            <button
              type="button"
              onClick={() => setOpenLoans(!openLoans)}
              className={`px-3 py-2 text-sm font-heading inline-flex items-center gap-1 transition-colors ${openLoans ? "text-[#7A4E1D]" : "text-ink-soft hover:text-[#7A4E1D]"}`}
              data-testid="debt-nav-loans-toggle"
              aria-haspopup="true"
              aria-expanded={openLoans}
            >
              Loans <ChevronDown size={13} className={openLoans ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {openLoans && (
              <div
                className="absolute left-0 top-full mt-1 w-[560px] rounded-md border border-[#B8860B]/20 bg-white shadow-[0_18px_40px_rgba(122,78,29,0.15)] py-3 grid grid-cols-2 gap-1"
                data-testid="debt-nav-loans-panel"
              >
                {LOAN_GROUPS.map((g) => (
                  <div key={g.key} className="px-2">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7A4E1D] px-2 pb-1.5">{g.heading}</div>
                    {g.items.map((it) => (
                      <a
                        key={`${g.key}-${it.label}`}
                        href={it.hash}
                        onClick={close}
                        className="block px-2 py-1.5 rounded hover:bg-[#FBFAF5] group"
                        data-testid={`debt-nav-loan-${g.key}-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      >
                        <div className="text-sm font-heading text-ink group-hover:text-[#7A4E1D]">{it.label}</div>
                        <div className="text-[11px] text-ink-muted">{it.sub}</div>
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <a href="#emi" onClick={close} className="px-3 py-2 text-sm font-heading text-ink-soft hover:text-[#7A4E1D] transition-colors" data-testid="debt-nav-emi">EMI Calculator</a>
          <Link to="/venturaz-money/products" onClick={close} className="px-3 py-2 text-sm font-heading text-ink-soft hover:text-[#7A4E1D] transition-colors" data-testid="debt-nav-products">Products</Link>
          <Link to="/venturaz-money/banks" onClick={close} className="px-3 py-2 text-sm font-heading text-ink-soft hover:text-[#7A4E1D] transition-colors" data-testid="debt-nav-banks">Lenders</Link>
          <Link to="/venturaz-money/become-partner" onClick={close} className="px-3 py-2 text-sm font-heading text-ink-soft hover:text-[#7A4E1D] transition-colors" data-testid="debt-nav-partner">Become Partner</Link>
          <a href="#faq" onClick={close} className="px-3 py-2 text-sm font-heading text-ink-soft hover:text-[#7A4E1D] transition-colors" data-testid="debt-nav-faq">FAQs</a>
        </nav>

        {/* Right CTAs */}
        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              <Link to={dashLink} onClick={close} className="text-sm text-ink font-heading hover:text-[#7A4E1D] transition-colors px-3" data-testid="debt-nav-my-dashboard">Dashboard</Link>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="px-3 py-2 text-sm bg-white border border-navy/15 text-ink hover:border-[#B8860B] hover:text-[#7A4E1D] transition-colors rounded-md"
                data-testid="debt-nav-logout-btn"
              >Sign Out</button>
            </>
          ) : (
            <Link to="/money/login" onClick={close} className="text-sm text-ink font-heading hover:text-[#7A4E1D] transition-colors px-3" data-testid="debt-nav-login">Login</Link>
          )}
          <a
            href="#lead-form"
            onClick={close}
            className="inline-flex items-center gap-1.5 px-5 py-2 bg-[#7A4E1D] text-[#FFF3D9] text-sm font-heading font-semibold hover:bg-[#5D3B15] transition-colors rounded-md shadow-[0_8px_24px_rgba(122,78,29,0.28)]"
            data-testid="debt-nav-cta"
          >
            <PhoneCall size={14} /> Get free consultation
          </a>
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setOpenMobile(!openMobile)} className="lg:hidden text-ink" data-testid="debt-mobile-menu-toggle">
          {openMobile ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {openMobile && (
        <div className="lg:hidden bg-white border-t border-[#B8860B]/25" data-testid="debt-mobile-menu-panel">
          <div className="px-6 py-4 flex flex-col gap-4">
            {LOAN_GROUPS.map((g) => (
              <div key={g.key}>
                <div className="text-[10px] font-mono tracking-[0.28em] uppercase text-[#7A4E1D] mb-2">{g.heading}</div>
                <div className="flex flex-col gap-1">
                  {g.items.map((it) => (
                    <a
                      key={`${g.key}-m-${it.label}`}
                      href={it.hash}
                      onClick={close}
                      className="text-sm font-heading py-1 text-ink"
                      data-testid={`debt-nav-m-loan-${g.key}-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >{it.label}</a>
                  ))}
                </div>
              </div>
            ))}
            <div className="h-px bg-navy/10" />
            <a href="#emi" onClick={close} className="text-sm text-ink font-heading" data-testid="debt-nav-m-emi">EMI Calculator</a>
            <Link to="/venturaz-money/products" onClick={close} className="text-sm text-ink font-heading" data-testid="debt-nav-m-products">Products</Link>
            <Link to="/venturaz-money/banks" onClick={close} className="text-sm text-ink font-heading" data-testid="debt-nav-m-banks">Lenders</Link>
            <Link to="/venturaz-money/become-partner" onClick={close} className="text-sm text-ink font-heading" data-testid="debt-nav-m-partner">Become Partner</Link>
            <a href="#how" onClick={close} className="text-sm text-ink font-heading" data-testid="debt-nav-m-how">How It Works</a>
            <a href="#faq" onClick={close} className="text-sm text-ink font-heading" data-testid="debt-nav-m-faq">FAQs</a>
            <div className="h-px bg-navy/10" />
            {user ? (
              <>
                <Link to={dashLink} onClick={close} className="text-ink font-heading text-sm" data-testid="debt-nav-m-dashboard">Dashboard</Link>
                <button onClick={async () => { await logout(); close(); nav("/"); }} className="text-left text-ink-soft font-heading text-sm" data-testid="debt-nav-m-logout">Sign Out</button>
              </>
            ) : (
              <Link to="/money/login" onClick={close} className="text-ink font-heading text-sm" data-testid="debt-nav-m-login">Login</Link>
            )}
            <a
              href="#lead-form"
              onClick={close}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-[#7A4E1D] text-[#FFF3D9] text-sm font-heading font-semibold rounded-md"
              data-testid="debt-nav-m-cta"
            >
              <PhoneCall size={14} /> Get free consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
